import {
	CompanyCreditReport,
	companyContactId,
	companyCreditReportFailureReasonSchema,
	companyCreditReportId,
	companyCreditReportVendorSchema,
	currencySchema,
	IndividualCreditReport,
	individualContactId,
	individualCreditReportFailureReasonSchema,
	individualCreditReportId,
	individualCreditReportVendorSchema,
	individualOFACCheckId,
	individualOfacCheckFailureReasonSchema,
	isoDateTimeSchema,
} from "@inrev/common";
import * as z from "zod";
import { defineTool } from "../tool-definition";

const earlierPullCount = z.number().int().nonnegative();

/** Experian's executive summary without the tax id it carries. */
export const executiveElementsSchema = z.strictObject(
	CompanyCreditReport.Vendor.experianExecutiveElementsSchema.omit({ taxId: true }).shape,
);

const companyFindingsSchema = z.strictObject({
	name: z.string().nullable(),
	experianBin: z.string().nullable(),
	experianFsr: z.number().nullable(),
	experianFsrFactors: z.array(
		z.strictObject({ code: z.string(), definition: z.string().nullable() }),
	),
	experianFsrTrends: z.array(
		z.strictObject({
			quarter: z.string(),
			score: z.number().nullable(),
			industryScore: z.number().nullable(),
		}),
	),
	experianExecutiveElements: executiveElementsSchema.nullable(),
	experianInquiries: z.array(
		z.strictObject({
			businessCategory: z.string().nullable(),
			counts: z.array(
				z.strictObject({
					year: z.number().nullable(),
					month: z.number().nullable(),
					count: z.number().nullable(),
				}),
			),
		}),
	),
	experianBusinessFacts: z
		.strictObject({
			fileEstablishedDate: z.string().nullable(),
			dateOfIncorporation: z.string().nullable(),
		})
		.nullable(),
	bankruptcy: z.boolean().nullable(),
	judgmentBalance: currencySchema.nullable(),
	lienBalance: currencySchema.nullable(),
	yearFoundedOrCreditEstablished: z.number().nullable(),
});

const companyPartySchema = z.strictObject({
	partyType: z.literal("company"),
	partyId: companyContactId,
	latestPull: z.strictObject({
		creditPullId: companyCreditReportId,
		succeeded: z.boolean(),
		failureReason: companyCreditReportFailureReasonSchema.nullable(),
		vendor: companyCreditReportVendorSchema.nullable(),
		createdAt: isoDateTimeSchema,
		profileUnreadable: z.boolean(),
		findings: companyFindingsSchema.nullable(),
	}),
	earlierPullCount,
});

/*
 * Readable Experian fields only. Coded fields (status, kind of business, evaluation, type) stay
 * out until we can decode them, and so do the consumer's identifiers and contact details. Creditor,
 * lender and plaintiff names are businesses, not the consumer, so they are sent. Three one-letter
 * flags whose field names spell out their values are sent as words.
 */
export const individualTradeSummarySchema = z.strictObject(
	IndividualCreditReport.Vendor.experianTradeSummarySchema.shape,
);

export const individualDerogatorySummarySchema = z.strictObject(
	IndividualCreditReport.Vendor.experianDerogatorySummarySchema.shape,
);

export const individualPublicRecordSchema = z.strictObject({
	...IndividualCreditReport.Vendor.experianPublicRecordSchema.pick({
		filingDate: true,
		statusDate: true,
		amount: true,
		courtName: true,
		plaintiffName: true,
		referenceNumber: true,
		bankruptcyAssetAmount: true,
		repaymentPercent: true,
		adjustmentPercent: true,
	}).shape,
	bankruptcyVoluntaryIndicator: z.enum(["voluntary"]).nullable(),
});

/** A tradeline with its enhanced payment fields flattened in. */
export const individualTradelineSchema = z.strictObject({
	...IndividualCreditReport.Vendor.experianTradelineSchema.pick({
		openDate: true,
		statusDate: true,
		balanceDate: true,
		balanceAmount: true,
		amountPastDue: true,
		monthlyPaymentAmount: true,
		lastPaymentDate: true,
		maxDelinquencyDate: true,
		delinquencies30Days: true,
		delinquencies60Days: true,
		delinquencies90to180Days: true,
		subscriberName: true,
		originalCreditorName: true,
		soldToName: true,
		monthsHistory: true,
		terms: true,
	}).shape,
	openOrClosed: z.enum(["open", "closed"]).nullable(),
	revolvingOrInstallment: z.enum(["revolving", "installment"]).nullable(),
	...IndividualCreditReport.Vendor.experianEnhancedPaymentDataSchema.pick({
		chargeoffAmount: true,
		creditLimitAmount: true,
		highBalanceAmount: true,
		originalLoanAmount: true,
		firstDelinquencyDate: true,
		purchasedPortfolioFromName: true,
	}).shape,
});

export const individualInquirySchema = z.strictObject(
	IndividualCreditReport.Vendor.experianInquirySchema.pick({
		date: true,
		amount: true,
		subscriberName: true,
	}).shape,
);

export const individualStatementSchema = z.strictObject(
	IndividualCreditReport.Vendor.experianStatementSchema.pick({
		dateReported: true,
		statementText: true,
	}).shape,
);

export const individualFraudShieldSchema = z.strictObject(
	IndividualCreditReport.Vendor.experianFraudShieldSchema.pick({ text: true }).shape,
);

const individualExperianSchema = z.strictObject({
	tradeSummary: individualTradeSummarySchema.nullable(),
	derogatorySummary: individualDerogatorySummarySchema.nullable(),
	publicRecords: z.array(individualPublicRecordSchema),
	tradelines: z.array(individualTradelineSchema),
	inquiries: z.array(individualInquirySchema),
	statements: z.array(individualStatementSchema),
	fraudShield: z.array(individualFraudShieldSchema),
});

const individualPartySchema = z.strictObject({
	partyType: z.literal("individual"),
	partyId: individualContactId,
	latestPull: z
		.strictObject({
			creditPullId: individualCreditReportId,
			succeeded: z.boolean(),
			failureReason: individualCreditReportFailureReasonSchema.nullable(),
			vendor: individualCreditReportVendorSchema.nullable(),
			createdAt: isoDateTimeSchema,
			profileUnreadable: z.boolean(),
			score: z.number().nullable(),
			bankruptcy: z.boolean().nullable(),
			experian: individualExperianSchema.nullable(),
		})
		.nullable(),
	latestScoredPull: z
		.strictObject({
			creditPullId: individualCreditReportId,
			score: z.number(),
			bankruptcy: z.boolean().nullable(),
			createdAt: isoDateTimeSchema,
		})
		.nullable(),
	earlierPullCount,
	latestOfac: z
		.strictObject({
			ofacCheckId: individualOFACCheckId,
			hit: z.boolean(),
			succeeded: z.boolean(),
			failureReason: individualOfacCheckFailureReasonSchema.nullable(),
			createdAt: isoDateTimeSchema,
		})
		.nullable(),
	earlierOfacCheckCount: earlierPullCount,
});

export const getCredit = defineTool({
	name: "get_credit",
	description:
		"The latest credit pull per party, for parties that have one; `partyId` matches `get_parties`. Company pulls carry the Experian FSR with its factors and quarterly trends (with the industry score), BIN, bankruptcy, judgment and lien balances, year founded, Experian's executive summary counts, inquiry counts by business category and month, and the file-established and incorporation dates. Individual `latestPull` is the latest attempt, failed or not, and carries score and bankruptcy, plus, for Experian pulls, `experian`: the trade and derogatory summaries, public records, tradelines, inquiries, Experian Fraud Shield messages, and the consumer's own statements. Individual Experian values are text as Experian reported them: amounts and counts are zero-padded (`00001200` is 1,200), dates are MMDDYYYY, and loan `terms` are months or `REV` revolving, `UNK` unknown. Individual public records hold bankruptcies; consumer files no longer carry judgments or tax liens, so an empty list is not proof of a clean record. Fraud Shield messages are alerts to verify and consumer statements are the consumer's own claims; neither is a finding. Scores from different vendors use different models, so do not read a change across vendors as a trend. Each individual also carries `latestScoredPull`, the latest successful pull with a score (the one the account report shows), so a failed latest pull still leaves the last known score; it is the same pull as `latestPull` when that one succeeded. Each individual also carries the latest OFAC screen, where `hit: true` is a sanctions match. `vendor` says who ran the pull; CIC and Meridian pulls carry fewer fields, so a null there means the vendor did not report it. `succeeded: false` with a `failureReason` means the pull ran and found nothing usable. `profileUnreadable` means the stored result could not be read, so its findings (and possibly `vendor`) are null. `earlierPullCount` counts older pulls not shown. No field is an SSN, date of birth, account number, tax id, address or phone number; consumer statements are free text, passed as written.",
	label: "Credit",
	contexts: ["account", "bond_request", "bond"],
	implementation: "api",
	audience: "model",
	input: z.strictObject({}),
	data: z.strictObject({
		parties: z.array(
			z.discriminatedUnion("partyType", [companyPartySchema, individualPartySchema]),
		),
	}),
});
